import {
  Badge,
  Button,
  Card,
  CardBody,
  IconButton,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { CapitalCallReallocationCreateModal } from "features/capital-activity/event/reallocation/CapitalCallReallocationCreateModal";
import { useDocumentRetrieve } from "gen/endpoints/documents/documents";
import {
  useIlpaNoticeExcelWorkbookCreate,
  useNoticeDocumentGenerationStatusRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useNoticeConfigRetrieve } from "gen/endpoints/notice-config/notice-config";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { GenericTableBadgeColorEnum } from "gen/model/genericTableBadgeColorEnum";
import { NoticeStatusEnum } from "gen/model/noticeStatusEnum";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import useDocumentPoll from "shared/hooks/useDocumentPoll";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { EditIcon } from "shared/icons/EditIcon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";

export interface CapitalEventStatsCardProps {
  capitalActivityData: FundFamilyEventData;
  isDraftEvent?: boolean;
  openEditDateModal?: () => void;
  isEqualizedView?: boolean;
  setIsEqualized?: (value: boolean) => void;
  waterfallFundFamilySubEventId?: string;
  confirmDeleteWithholding?: () => void;
  openWithholdingModal?: () => void;
  fundFamilyId?: string;
  fundFamilyEventId?: string;
  showWithholdings?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  showReallocation?: boolean;
}

const getColorForNoticeStatus = (
  status: NoticeStatusEnum,
): GenericTableBadgeColorEnum => {
  switch (status) {
    case NoticeStatusEnum.successful_generation:
      return "primary";
    case NoticeStatusEnum.failed_generation:
      return "error";
    case NoticeStatusEnum.out_of_date:
      return "warning";
    case NoticeStatusEnum.generating:
      return "blue";
    default:
      return "default";
  }
};

export const CapitalEventStatsCard: React.FC<CapitalEventStatsCardProps> = ({
  capitalActivityData,
  isDraftEvent,
  openEditDateModal,
  setIsEqualized,
  isEqualizedView,
  waterfallFundFamilySubEventId,
  confirmDeleteWithholding,
  openWithholdingModal,
  fundFamilyId,
  fundFamilyEventId,
  showWithholdings,
  onEdit,
  onDelete,
  showReallocation,
}) => {
  const { t } = useTranslation([
    "capitalActivity",
    "capitalCall",
    "common",
    "notices",
    "enums",
  ]);
  const { data: noticeConfigData } = useNoticeConfigRetrieve(
    capitalActivityData.fund_family_id,
    {
      query: {
        retry: false,
        refetchOnWindowFocus: false,
        // this endpoint returns a 404 if notices are not enabled
        throwOnError: false,
      },
    },
  );
  const isNoticesActive = !!noticeConfigData?.is_enabled;
  const isILPANoticeWorkbookEnabled =
    !!noticeConfigData?.ilpa_notice_workbook_is_enabled;

  const navigate = useNavigate();
  const noticesPath = useMemo(
    () =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId: capitalActivityData.fund_family_id })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId: capitalActivityData.id! })
        .notices({}).$,
    [capitalActivityData],
  );

  const { data: noticeDocumentGenerationStatusData } =
    useNoticeDocumentGenerationStatusRetrieveSuspense(
      capitalActivityData.fund_family_id,
      capitalActivityData.id!,
    );

  const { mutateAsync: createILPANoticeWorkbook } =
    useIlpaNoticeExcelWorkbookCreate();

  const { setDocumentId } = useDocumentPoll(
    t("notices:ilpaNoticeWorkbook.success"),
    t("notices:ilpaNoticeWorkbook.error"),
    t("notices:ilpaNoticeWorkbook.start"),
  );

  const { data: withholdingDocumentData } = useDocumentRetrieve(
    capitalActivityData.distribution_withholding_upload_document_id ?? "",
    {
      query: {
        enabled:
          !!capitalActivityData.distribution_withholding_upload_document_id,
      },
    },
  );

  const {
    isOpen: isCreateReallocationModalOpen,
    onOpen: openReallocationModal,
    onClose: closeReallocationModal,
  } = useDisclosure();

  const isWaterfallSubeventActive = useFeatureFlagActive("waterfall_subevent");
  // can only add withholding if all waterfalls have been run and are up to date
  // and if there is a distribution
  const canAddWithholding =
    !!isWaterfallSubeventActive &&
    !!waterfallFundFamilySubEventId &&
    showWithholdings;

  const hasCapitalCallDueDateOverride =
    !!capitalActivityData.capital_call_effective_date;
  const hasCapitalCallNoticeDateOverride =
    !!capitalActivityData.capital_call_posted_date;

  const showReallocateModel = !isDraftEvent && showReallocation;

  return (
    <Card variant="stat" size="xs">
      <CardBody>
        <CardSectionGroup flexWrap="wrap">
          <CardSection flexGrow="1">
            <CardSectionBody alignItems="stretch">
              <StatGroup justifyContent="flex-start">
                {hasCapitalCallNoticeDateOverride && (
                  <Stat>
                    <StatLabel>
                      {t("capitalActivity:dates.capitalCallInvestorNoticeDate")}
                    </StatLabel>
                    <StatNumber>
                      <Badge>
                        {formatIsoDateToDisplay(
                          capitalActivityData.capital_call_posted_date,
                        )}
                      </Badge>
                    </StatNumber>
                  </Stat>
                )}
                {hasCapitalCallDueDateOverride && (
                  <Stat>
                    <StatLabel>
                      {t("capitalActivity:dates.capitalCallDueDate")}
                    </StatLabel>
                    <StatNumber>
                      <Badge>
                        {formatIsoDateToDisplay(
                          capitalActivityData.capital_call_effective_date,
                        )}
                      </Badge>
                    </StatNumber>
                  </Stat>
                )}
                <Stat>
                  <StatLabel>
                    {hasCapitalCallNoticeDateOverride
                      ? t(
                          "capitalActivity:dates.distributionInvestorNoticeDate",
                        )
                      : t("capitalActivity:dates.investorNoticeDate")}
                  </StatLabel>
                  <StatNumber>
                    <Badge>
                      {formatIsoDateToDisplay(capitalActivityData.posted_date)}
                    </Badge>
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>
                    {hasCapitalCallDueDateOverride
                      ? t("capitalActivity:dates.distributionDueDate")
                      : t("capitalActivity:dates.dueDate")}
                  </StatLabel>
                  <StatNumber>
                    <Badge>
                      {formatIsoDateToDisplay(
                        capitalActivityData.effective_date,
                      )}
                    </Badge>
                  </StatNumber>
                </Stat>
                {(isDraftEvent || capitalActivityData.notes) && (
                  <Stat flex="1" flexBasis="max-content" maxWidth="400px">
                    <StatLabel>{t("common:notesOptional")}</StatLabel>
                    <Text whiteSpace="normal">
                      {capitalActivityData.notes || "-"}
                    </Text>
                  </Stat>
                )}
              </StatGroup>
              {isDraftEvent && openEditDateModal && (
                <IconButton
                  mr={isNoticesActive ? "-24px" : "-32px"}
                  p="24px"
                  aria-label="edit"
                  size="sm"
                  variant="ghost"
                  borderRadius={0}
                  icon={<EditIcon w="20px" h="20px" color="gray.400" />}
                  onClick={openEditDateModal}
                />
              )}
            </CardSectionBody>
          </CardSection>
          {!!isDraftEvent &&
            isWaterfallSubeventActive &&
            confirmDeleteWithholding &&
            openWithholdingModal && (
              <CardSection flexGrow="1" flexBasis="max-content" flexShrink="1">
                <CardSectionBody>
                  <StatGroup justifyContent="flex-start">
                    <Stat>
                      <Tooltip
                        label={t("distribution:withholding.button.tooltip")}
                        isDisabled={
                          !!canAddWithholding || !!withholdingDocumentData
                        }
                      >
                        <Button
                          colorScheme={
                            withholdingDocumentData ? "tertiary" : "secondary"
                          }
                          onClick={() =>
                            withholdingDocumentData
                              ? confirmDeleteWithholding()
                              : openWithholdingModal()
                          }
                          isDisabled={
                            !canAddWithholding && !withholdingDocumentData
                          }
                        >
                          {withholdingDocumentData
                            ? t("distribution:withholding.button.remove")
                            : t("distribution:withholding.button.add")}
                        </Button>
                      </Tooltip>
                    </Stat>
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            )}
          {(isNoticesActive || isILPANoticeWorkbookEnabled) && (
            <CardSection flexGrow="1" flexBasis="max-content" flexShrink="1">
              <CardSectionBody>
                <StatGroup justifyContent="flex-start">
                  {isNoticesActive && isDraftEvent && (
                    <Stat size="xs" flexGrow="0">
                      <StatLabel>{t("notices:noticeStatus")}</StatLabel>
                      <Badge
                        colorScheme={getColorForNoticeStatus(
                          noticeDocumentGenerationStatusData.status,
                        )}
                      >
                        {t(
                          `enums:NoticeStatusEnum.${noticeDocumentGenerationStatusData.status}`,
                        )}
                      </Badge>
                    </Stat>
                  )}
                  {isNoticesActive && (
                    <Button
                      size="sm"
                      colorScheme="secondary"
                      alignSelf="center"
                      onClick={() => navigate(noticesPath)}
                    >
                      {isDraftEvent
                        ? t("notices:generateNotices")
                        : t("notices:viewNotices")}
                    </Button>
                  )}
                  {isILPANoticeWorkbookEnabled && (
                    <Button
                      size="sm"
                      colorScheme="secondary"
                      alignSelf="center"
                      onClick={async () => {
                        const workbookDocument = await createILPANoticeWorkbook(
                          {
                            fundFamilyId: capitalActivityData.fund_family_id,
                            fundFamilyEventId: capitalActivityData.id!,
                          },
                        );
                        setDocumentId(workbookDocument.id);
                      }}
                    >
                      {t("notices:ilpaNoticeWorkbook.downloadButton")}
                    </Button>
                  )}
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          )}
          {showReallocateModel && (
            <CardSection flexGrow="1" flexBasis="max-content" flexShrink="1">
              <CardSectionBody>
                <StatGroup justifyContent="flex-start">
                  <Button
                    colorScheme="tertiary"
                    onClick={openReallocationModal}
                  >
                    {t("capitalCall:reallocation.reallocateUses")}
                  </Button>
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          )}
          {isCreateReallocationModalOpen && (
            <CapitalCallReallocationCreateModal
              onCloseComplete={closeReallocationModal}
            />
          )}
          {isDraftEvent && (onEdit || onDelete) && (
            <CardSection flexGrow="1" flexBasis="max-content" flexShrink="1">
              <CardSectionBody>
                <StatGroup justifyContent="flex-start">
                  {onDelete && (
                    <Button colorScheme="destructive" onClick={onDelete}>
                      {t("common:discard")}
                    </Button>
                  )}
                  {onEdit && (
                    <Button colorScheme="tertiary" onClick={onEdit}>
                      {t("common:edit")}
                    </Button>
                  )}
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          )}
          {isWaterfallSubeventActive &&
            !isDraftEvent &&
            waterfallFundFamilySubEventId &&
            fundFamilyId &&
            fundFamilyEventId && (
              <CardSection flexGrow="1" flexBasis="max-content" flexShrink="1">
                <CardSectionBody>
                  <StatGroup justifyContent="flex-start">
                    <Button
                      colorScheme="tertiary"
                      onClick={() =>
                        navigate(
                          rootRoute({})
                            .fundFamilies({})
                            .fundFamily({ fundFamilyId })
                            .capitalActivity({})
                            .capitalActivityEvent({ fundFamilyEventId })
                            .waterfall({})
                            .review({
                              fundFamilySubEventId:
                                waterfallFundFamilySubEventId,
                            }).$,
                        )
                      }
                    >
                      {t("capitalActivity:waterfalls.view")}
                    </Button>
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            )}
          {!isDraftEvent && setIsEqualized && isEqualizedView != undefined && (
            <CardSection
              flexGrow="1"
              flexBasis="max-content"
              flexShrink="1"
              px="16px"
            >
              <CardSectionBody>
                <EqualizedViewToggleButton
                  isEqualizedView={isEqualizedView}
                  setIsEqualizedView={setIsEqualized}
                />
              </CardSectionBody>
            </CardSection>
          )}
        </CardSectionGroup>
      </CardBody>
    </Card>
  );
};
